import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppNotification } from '@neuroedge/shared';
import { PageState } from '../ui';
import { useDesktopAlerts } from './desktop-alerts';

const footerLink = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  padding: 0,
} as const;
import {
  useUnreadCount,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../api/notifications';

function when(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

/** Where clicking a notification takes you - best-effort, no deep-linking into Inbox yet. */
function entityHref(n: AppNotification): string | null {
  if (n.entityType === 'conversation') return '/conversations';
  if (n.entityType === 'lead' && n.entityId) return `/leads/${n.entityId}`;
  if (n.entityType === 'invoice' || n.entityType === 'subscription') return '/billing';
  if (n.entityType === 'task') return '/tasks';
  if (n.entityType === 'follow_up') return '/follow-ups';
  if (n.entityType === 'order') return '/orders';
  if (n.entityType === 'appointment') return '/appointments';
  if (n.entityType === 'quotation') return '/quotations';
  if (n.entityType === 'channel') return '/channels';
  if (n.entityType === 'campaign' && n.entityId) return `/campaigns/${n.entityId}`;
  // A background quotation-generation job - the Quotations page's "Ongoing
  // quotations" panel surfaces it (progress if still running, or the
  // error/warning once it's finished).
  if (n.entityType === 'quotation_generation_job') return '/quotations';
  // Same idea for the AI catalog-import job - the Products & Services
  // page's "Ongoing catalog imports" panel surfaces it.
  if (n.entityType === 'catalog_import_job') return '/products-services';
  return null;
}

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: AppNotification;
  onOpen: (n: AppNotification) => void;
}) {
  const unread = notification.readAt === null;
  return (
    <button
      type="button"
      onClick={() => onOpen(notification)}
      className="ne-notif__row"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-3)',
        border: 'none',
        borderBottom: '1px solid var(--color-border)',
        background: unread ? 'var(--color-bg)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        {unread ? (
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--color-primary)',
              flexShrink: 0,
            }}
          />
        ) : null}
        <strong style={{ fontSize: 'var(--font-size-sm)' }}>{notification.title}</strong>
      </div>
      {notification.body ? (
        <div
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {notification.body}
        </div>
      ) : null}
      <div
        style={{
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-xs, 11px)',
          marginTop: 2,
        }}
      >
        {when(notification.createdAt)}
      </div>
    </button>
  );
}

/**
 * Topbar notification bell + unread badge. Unread count polls continuously
 * (cheap, `GET /notifications/unread-count`); the dropdown's own list only
 * fetches while open. What creates a notification and who gets it lives in
 * the API's `notifications/notification-log.ts` + `reminders.ts`; each user
 * mutes categories on their Profile page. Optional desktop pop-ups
 * (`desktop-alerts.ts`) are switched on from the dropdown's footer.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const list = useNotifications({ pageSize: 10 }, open);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  const count = unread.data?.count ?? 0;

  const onOpenNotification = useCallback(
    async (n: AppNotification) => {
      if (n.readAt === null) {
        try {
          await markRead.mutateAsync(n.id);
        } catch {
          // Best-effort - still navigate even if marking read failed.
        }
      }
      setOpen(false);
      const href = entityHref(n);
      if (href) navigate(href);
    },
    [markRead, navigate],
  );
  const alerts = useDesktopAlerts(
    unread.data?.count,
    useCallback((n: AppNotification) => void onOpenNotification(n), [onOpenNotification]),
  );

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={count > 0 ? `${count} unread notifications` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          background: 'none',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          width: 36,
          height: 36,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 16,
        }}
      >
        🔔
        {count > 0 ? (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              padding: '0 3px',
              borderRadius: 8,
              background: 'var(--color-danger)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            style={{ position: 'fixed', inset: 0, zIndex: 1200 }}
          />
          <div
            role="dialog"
            aria-label="Notifications"
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + var(--space-2))',
              width: 'min(360px, 90vw)',
              maxHeight: 420,
              overflowY: 'auto',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              zIndex: 1201,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-3)',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <strong style={{ fontSize: 'var(--font-size-sm)' }}>Notifications</strong>
              {count > 0 ? (
                <button
                  type="button"
                  onClick={() => void markAllRead.mutateAsync()}
                  disabled={markAllRead.isPending}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                    padding: 0,
                  }}
                >
                  Mark all read
                </button>
              ) : null}
            </div>

            {list.isPending ? (
              <PageState kind="loading" message="Loading…" />
            ) : list.isError ? (
              <PageState kind="error" message={list.error.message} />
            ) : list.data.items.length === 0 ? (
              <PageState kind="empty" title="No notifications" />
            ) : (
              list.data.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onOpen={(x) => void onOpenNotification(x)}
                />
              ))
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {alerts.supported ? (
                alerts.blocked ? (
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    Desktop alerts are blocked in this browser's site settings.
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => (alerts.enabled ? alerts.disable() : void alerts.enable())}
                    style={footerLink}
                  >
                    {alerts.enabled ? 'Turn off desktop alerts' : 'Turn on desktop alerts'}
                  </button>
                )
              ) : (
                <span />
              )}
              <button
                type="button"
                style={footerLink}
                onClick={() => {
                  setOpen(false);
                  navigate('/profile#notifications');
                }}
              >
                Notification settings
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
